/**
 * DonutChart Component
 *
 * Recharts PieChart wrapper configured as a donut chart with Safaqat 24 styling.
 */

import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from 'recharts';
import { formatAmount } from '../../utils/numberFormat';

export interface DonutChartData {
  name: string;
  value: number;
  color: string;
}

export interface DonutChartProps {
  data: DonutChartData[];
  height?: number;
  innerRadius?: number;
  outerRadius?: number;
  showLegend?: boolean;
  showLabels?: boolean;
  centerLabel?: string;
  centerValue?: string | number;
  className?: string;
}

export default function DonutChart({
  data,
  height = 300,
  innerRadius = 60,
  outerRadius = 100,
  showLegend = true,
  showLabels = false,
  centerLabel,
  centerValue,
  className = '',
}: DonutChartProps) {
  const total = data.reduce((sum, item) => sum + item.value, 0);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const entry = payload[0];
      const percentage = ((entry.value / total) * 100).toFixed(1);
      return (
        <div className="bg-white dark:bg-[var(--pv-color-surface-strong)] p-3 rounded-xl shadow-lg border border-[var(--pv-border-soft)]">
          <p className="text-sm font-medium text-[var(--pv-color-text)]" style={{ color: entry.payload.color }}>
            {entry.name}
          </p>
          <p className="text-sm text-[var(--pv-color-text-muted)]">
            {formatAmount(entry.value)} ({percentage}%)
          </p>
        </div>
      );
    }
    return null;
  };

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }: any) => {
    if (!showLabels) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 1.3;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (percent < 0.05) return null; // Don't show labels for small slices

    return (
      <text
        x={x}
        y={y}
        fill="var(--pv-color-text-muted)"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={12}
      >
        {name} ({(percent * 100).toFixed(0)}%)
      </text>
    );
  };

  return (
    <div className={`relative ${className}`} style={{ direction: 'ltr' }}>
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            paddingAngle={2}
            dataKey="value"
            label={showLabels ? renderCustomLabel : false}
            labelLine={false}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          {showLegend && (
            <Legend
              layout="vertical"
              align="right"
              verticalAlign="middle"
              formatter={(value, entry: any) => (
                <span className="text-sm text-[var(--pv-color-text)]">
                  {value} ({formatAmount(entry.payload.value)})
                </span>
              )}
            />
          )}
        </PieChart>
      </ResponsiveContainer>

      {/* Center label */}
      {(centerLabel || centerValue) && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            {centerValue && (
              <div className="text-2xl font-bold text-[var(--pv-color-text)]">
                {typeof centerValue === 'number'
                  ? formatAmount(centerValue)
                  : centerValue}
              </div>
            )}
            {centerLabel && (
              <div className="text-sm text-[var(--pv-color-text-muted)]">{centerLabel}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
